import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type FormEvent, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { AccessError, AccessFrame } from "../../professional/access/AccessFrame";
import { MfaStep } from "../../professional/access/MfaStep";
import { ReviewerAccountPage } from "../../professional/access/ReviewerAccountPage";
import { useUiTelemetry } from "../telemetry";
import type { MfaChallenge, ReviewerAccount } from "./reviewer-api";

export interface ProfessionalSession extends Partial<Omit<ReviewerAccount, "authenticated">> {
  authenticated: boolean;
  mfa_required?: boolean;
  enrollment_required?: boolean;
}

interface AuthContextValue extends ProfessionalSession {
  loading: boolean;
  initialised: boolean;
  signingOut: boolean;
  clearanceEpoch: number;
  error: string | null;
  refresh: (parentSignal?: AbortSignal) => Promise<void>;
  expire: () => void;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export const PROFESSIONAL_AUTH_TIMEOUT_MS = 30_000;

class AuthTimeoutError extends Error {
  constructor() { super("The professional access request timed out. Your saved case and entered details have been kept. Try again."); this.name = "AuthTimeoutError"; }
}

async function boundedAuthOperation<T>(controller: AbortController, operation: (signal: AbortSignal) => Promise<T>): Promise<T> {
  const { signal } = controller;
  if (signal.aborted) throw new DOMException("The request was cancelled.", "AbortError");
  let onAbort = () => {};
  const cancelled = new Promise<T>((_resolve, reject) => {
    onAbort = () => { reject(signal.reason instanceof Error ? signal.reason : new DOMException("The request was cancelled.", "AbortError")); };
    signal.addEventListener("abort", onAbort, { once: true });
  });
  const timer = setTimeout(() => controller.abort(new AuthTimeoutError()), PROFESSIONAL_AUTH_TIMEOUT_MS);
  try { return await Promise.race([operation(signal), cancelled]); }
  finally { clearTimeout(timer); signal.removeEventListener("abort", onAbort); }
}

export function parseProfessionalSession(value: unknown): ProfessionalSession {
  if (typeof value !== "object" || value === null || !("authenticated" in value)) throw new Error("The server returned an invalid professional session.");
  const session = value as Record<string, unknown>;
  if (session.authenticated === false) return { authenticated: false, ...(session.mfa_required === true ? {mfa_required: true, enrollment_required: session.enrollment_required === true} : {}) };
  if (session.authenticated !== true || !["medical_professional", "reviewer_applicant", "umocare_admin"].includes(String(session.role)) || typeof session.user_id !== "string" || !session.user_id || typeof session.name !== "string" || typeof session.organisation !== "string" || session.mfa_enrolled !== true || typeof session.case_access !== "boolean") throw new Error("The server did not supply a verified professional identity.");
  if (session.role === "medical_professional" && (session.status !== "approved" || session.case_access !== true)) throw new Error("This account has not been approved for case access.");
  if (session.role !== "medical_professional" && session.case_access !== false) throw new Error("This account has an invalid case-access role.");
  if (session.role === "umocare_admin" && session.status !== "active") throw new Error("This staff account is not active.");
  if (session.role === "reviewer_applicant" && !["pending", "submitted", "changes_requested", "rejected", "suspended"].includes(String(session.status))) throw new Error("This application has an invalid review status.");
  return {...session, authenticated: true} as unknown as ProfessionalSession;
}

export function ProfessionalAuthProvider({ children }: { children: ReactNode }) {
  const { bindContext } = useUiTelemetry();
  const [session, setSession] = useState<ProfessionalSession>({ authenticated: false });
  const [loading, setLoading] = useState(true);
  const [initialised, setInitialised] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [signingOut, setSigningOut] = useState(false);
  const [clearanceEpoch, setClearanceEpoch] = useState(0);
  const refreshRequest = useRef<AbortController | null>(null);
  const signOutRequest = useRef<AbortController | null>(null);
  const signOutPromise = useRef<Promise<void> | null>(null);
  const expire = useCallback(() => {
    const previous = refreshRequest.current;
    refreshRequest.current = null;
    previous?.abort();
    setSession((current) => ({ ...current, authenticated: false }));
    setLoading(false);
    setInitialised(true);
  }, []);
  const refresh = useCallback(async (parentSignal?: AbortSignal) => {
    if (parentSignal?.aborted || signOutRequest.current) return;
    refreshRequest.current?.abort();
    const controller = new AbortController();
    const parentAborted = () => controller.abort();
    parentSignal?.addEventListener("abort", parentAborted, { once: true });
    refreshRequest.current = controller;
    setLoading(true);
    setError(null);
    try {
      const next = await boundedAuthOperation(controller, async (signal) => {
        const response = await fetch("/api/ui/professional/session", { credentials: "same-origin", cache: "no-store", signal });
        if (response.status === 401) { await response.body?.cancel(); return { authenticated: false }; }
        if (!response.ok) { await response.body?.cancel(); throw new Error("Professional access could not be checked. Your saved case is retained."); }
        return parseProfessionalSession(await response.json());
      });
      if (refreshRequest.current === controller && !controller.signal.aborted && !signOutRequest.current) setSession(next.authenticated ? next : (current) => ({...current, ...next}));
    } catch (reason: unknown) {
      if (refreshRequest.current === controller && (!controller.signal.aborted || reason instanceof AuthTimeoutError)) {
        setSession((current) => ({ ...current, authenticated: false }));
        setError(reason instanceof Error ? reason.message : "Professional access could not be checked. Your saved case is retained.");
      }
    } finally {
      parentSignal?.removeEventListener("abort", parentAborted);
      if (refreshRequest.current === controller) { refreshRequest.current = null; setLoading(false); setInitialised(true); }
    }
  }, []);
  const signOut = useCallback((): Promise<void> => {
    if (signOutPromise.current) return signOutPromise.current;
    const prior = refreshRequest.current;
    refreshRequest.current = null;
    prior?.abort();
    const controller = new AbortController();
    signOutRequest.current = controller;
    setSigningOut(true); setError(null);
    const operation = (async () => {
      try {
        await boundedAuthOperation(controller, async (signal) => {
          const response = await fetch("/api/ui/professional/sign-out", { method: "POST", credentials: "same-origin", cache: "no-store", headers: {"Content-Type": "application/json"}, body: "{}", signal });
          await response.body?.cancel();
          if (!response.ok) throw new Error("Sign-out could not be confirmed. Try signing out again.");
        });
        if (signOutRequest.current !== controller || controller.signal.aborted) return;
        setSession({authenticated: false});
        setClearanceEpoch((epoch) => epoch + 1);
        setLoading(false); setInitialised(true);
        void bindContext(null).catch(() => undefined);
      } catch (reason: unknown) {
        if (signOutRequest.current === controller) setError(reason instanceof AuthTimeoutError ? "Sign-out timed out. Server revocation has not been confirmed. Try again." : reason instanceof Error ? reason.message : "Sign-out could not be confirmed. Try again.");
        throw reason;
      } finally {
        if (signOutRequest.current === controller) { signOutRequest.current = null; setSigningOut(false); setLoading(false); setInitialised(true); }
        signOutPromise.current = null;
      }
    })();
    signOutPromise.current = operation;
    return operation;
  }, [bindContext]);
  useEffect(() => {
    void refresh();
    return () => { const pendingRefresh = refreshRequest.current; const pendingSignOut = signOutRequest.current; refreshRequest.current = null; signOutRequest.current = null; pendingRefresh?.abort(); pendingSignOut?.abort(); };
  }, [refresh]);
  const value = useMemo(() => ({ ...session, loading, initialised, signingOut, clearanceEpoch, error, refresh, expire, signOut }), [session, loading, initialised, signingOut, clearanceEpoch, error, refresh, expire, signOut]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useProfessionalAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (context === null) throw new Error("useProfessionalAuth must be used within ProfessionalAuthProvider.");
  return context;
}

export function ProfessionalSignIn({ returning = false }: { returning?: boolean }) {
  const { refresh, error: sessionError, mfa_required, enrollment_required } = useProfessionalAuth();
  const [challenge, setChallenge] = useState<MfaChallenge | null>(mfa_required ? {authenticated: false, mfa_required: true, enrollment_required: enrollment_required === true} : null);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const signInRequest = useRef<AbortController | null>(null);
  const errorRef = useRef<HTMLDivElement>(null);
  useEffect(() => () => { const controller = signInRequest.current; signInRequest.current = null; controller?.abort(); }, []);
  useEffect(() => { if (error ?? sessionError) errorRef.current?.focus(); }, [error, sessionError]);
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (signInRequest.current !== null) return;
    const controller = new AbortController();
    signInRequest.current = controller;
    const data = new FormData(event.currentTarget);
    setPending(true);
    setError(null);
    try {
      const response = await boundedAuthOperation(controller, async (signal) => {
        const received = await fetch("/api/ui/professional/sign-in", { method: "POST", credentials: "same-origin", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ username: data.get("username"), password: data.get("password") }), signal });
        if (!received.ok) { await received.body?.cancel(); return {accepted: false, challenge: null}; }
        return {accepted: true, challenge: await received.json() as Partial<MfaChallenge>};
      });
      if (signInRequest.current !== controller || controller.signal.aborted) return;
      if (!response.accepted) { setError("Sign-in was not accepted. Check your details and try again. Your saved case is retained."); return; }
      if (response.challenge?.mfa_required === true) setChallenge({authenticated: false, mfa_required: true, enrollment_required: response.challenge.enrollment_required === true});
      else await refresh(controller.signal);
    } catch (reason: unknown) {
      if (signInRequest.current === controller && (!controller.signal.aborted || reason instanceof AuthTimeoutError)) setError(reason instanceof AuthTimeoutError ? reason.message : "The sign-in service could not be reached. Your saved case is retained. Try again when the connection is available.");
    } finally { if (signInRequest.current === controller) { signInRequest.current = null; setPending(false); } }
  };
  const cancel = () => { const controller = signInRequest.current; signInRequest.current = null; controller?.abort(); setPending(false); setError("Sign-in was cancelled. Your entered details and saved case have been kept. You can try again."); };
  if (challenge) return <MfaStep enrollmentRequired={challenge.enrollment_required} onComplete={() => refresh()} onBack={() => setChallenge(null)} />;
  return <AccessFrame title={returning ? "Sign in to continue your review" : "Medical professional sign-in"}><p>{returning ? "Your session ended. Your saved case and current review position have been kept." : "Sign in with your individual UmoCare account. Your authenticator code is checked next."}</p>{error ?? sessionError ? <div ref={errorRef} className="error-summary" role="alert" tabIndex={-1}><h2>There is a problem</h2><p>{error ?? sessionError}</p></div> : null}<form onSubmit={(event) => { void submit(event); }}><div className="field"><label htmlFor="professional-username">Username or work email</label><input id="professional-username" name="username" autoComplete="username" required /></div><div className="field"><label htmlFor="professional-password">Password</label><input id="professional-password" name="password" type="password" autoComplete="current-password" required /></div><div className="reviewer-actions"><button className="button button-primary" type="submit" disabled={pending}>Sign in</button>{pending ? <><p role="status">Signing in</p><button type="button" onClick={cancel}>Cancel sign-in</button></> : null}</div></form><p className="reviewer-hint">New reviewer? Open the private UmoCare QR invitation shared with you by our staff.</p></AccessFrame>;
}

export function ProfessionalGuard({ children }: { children: ReactNode }) {
  const auth = useProfessionalAuth();
  const preserved = useRef({boundary: "", authorised: false});
  const boundary = `${auth.clearanceEpoch}:${auth.user_id ?? ""}`;
  if (preserved.current.boundary !== boundary) preserved.current = {boundary, authorised: false};
  const authorised = auth.authenticated && auth.role === "medical_professional" && auth.status === "approved" && auth.mfa_enrolled === true && auth.case_access === true;
  if (authorised) preserved.current.authorised = true;
  if (auth.loading && !auth.initialised && !preserved.current.authorised) return <AccessFrame title="Checking professional access"><p role="status">Checking your authorised role.</p></AccessFrame>;
  if (auth.authenticated && auth.role === "reviewer_applicant") return <ReviewerAccountPage />;
  if (auth.authenticated && auth.role === "umocare_admin") return <AccessFrame title="Staff administration"><p>Your staff account manages reviewer invitations and approval.</p><Link className="button button-primary" to="/professional/admin">Open staff workspace</Link></AccessFrame>;
  return <>{!authorised ? <ProfessionalSignIn returning={preserved.current.authorised} /> : null}{preserved.current.authorised ? <div key={boundary} hidden={!authorised}>{children}</div> : null}</>;
}

export function StaffGuard({ children }: {children: ReactNode}) {
  const auth = useProfessionalAuth();
  if (auth.loading && !auth.initialised) return <AccessFrame title="Checking staff access"><p role="status">Checking your staff account.</p></AccessFrame>;
  if (!auth.authenticated) return <ProfessionalSignIn />;
  if (auth.role !== "umocare_admin" || !auth.mfa_enrolled) return <AccessFrame title="Staff access required"><AccessError message="Only an authenticated UmoCare staff account can review applications and issue invitations." /><Link to="/professional/cases">Return to your account</Link></AccessFrame>;
  return <div key={`${auth.clearanceEpoch}:${auth.user_id}`}>{children}</div>;
}
